/**
 * DailyReportsTab — "דוחות אכילה" (nutrition module, Phase 7).
 *
 * The coach's side of the daily eating report: one card per day (newest first),
 * macro totals against the day's goals, and — the reason this screen exists —
 * a visible audit of what the CLIENT changed after logging a meal.
 *
 * Three rules this file is built around:
 *   1. Nothing is ever hidden. An entry the client removed (`deletedByClient`)
 *      renders struck-through, never filtered out, and every `edits[]` record
 *      renders as a compact before → after row.
 *   2. The coach can log on the client's behalf (`source: "coach"`) and correct
 *      any AI value. The SERVER writes the before/after audit — this component
 *      only sends `changes`.
 *   3. "סימון כנסקר" (+ an optional 1-5 rating) is what removes the day from the
 *      coach's pending queue, so it is a deliberate, labelled action.
 *
 * Goals fall back to `patient.nutrition.goals` when a day closed without a
 * snapshot. Goal math is always `calcGoalProgress` — never re-derived here.
 *
 * Error codes: CK-10200 (load), CK-10201 (add an entry on behalf),
 *              CK-10202 (edit an entry), CK-10203 (mark reviewed),
 *              CK-10204 (add a note), CK-10205 (delete a note).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ArrowRight,
  CaretDown,
  CheckCircle,
  ClockCountdown,
  ForkKnife,
  NotePencil,
  PencilSimple,
  Plus,
  Robot,
  Star,
  Trash,
  User,
  UserCircleGear,
  WarningDiamond,
} from "@phosphor-icons/react";

import { NOTE_TARGETS } from "../../../firebase";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import {
  useAddCoachFoodEntryMutation,
  useAddCoachNoteMutation,
  useDeleteCoachNoteMutation,
  useGetDailyReportsQuery,
  useMarkReportReviewedMutation,
  useUpdateFoodEntryMutation,
} from "../../../store/services/dailyReportsApi";
import { calcGoalProgress, sumNutrition } from "../../../utils/nutritionCalc";
import { formatHebrewDate } from "../../../utils/dateUtils";
import { useToast } from "../../../contexts/ToastContext";
import EmptyDraw from "../../ui/EmptyDraw";
import HoverTip from "../../ui/HoverTip";
import PersonalPageLink from "./PersonalPageLink";
import CoachNotesPanel from "../../nutrition/menu/CoachNotesPanel";
import "./DailyReportsTab.css";
import ContentLoader from "../../shared/ContentLoader";
import LoaderLoop from "../../shared/loader/LoaderLoop";

// ============================================================================
// Constants
// ============================================================================

/** How many days back the tab loads. Matches the server default. */
/** Exported so the workspace header can share this query's cache entry. */
export const REPORTS_LIMIT = 60;

/** The four macros, in the RTL display order coaches expect. */
const MACROS = [
  { key: "calories", label: "קלוריות", unit: 'קק"ל' },
  { key: "carbohydrates", label: "פחמימות", unit: "גרם" },
  { key: "protein", label: "חלבון", unit: "גרם" },
  { key: "fat", label: "שומן", unit: "גרם" },
];

const GOAL_STATUS_CLASS = {
  under: "under",
  on_target: "on-target",
  over: "over",
};

// A client edit is an annotation on the report, not a category of report —
// it is marked inline next to the changed field (see EditMark), never as a tab.
const FILTER_TABS = [
  { id: "all", label: "כל הימים", icon: ForkKnife },
  { id: "pending", label: "ממתינים לסקירה", icon: ClockCountdown },
];

/** Numeric columns of a food row, in display order. */
const FOOD_MACRO_FIELDS = [
  { key: "calories", label: 'קק"ל' },
  { key: "carbohydrates", label: "פחמימות" },
  { key: "protein", label: "חלבון" },
  { key: "fat", label: "שומן" },
];

const EDIT_FIELD_LABELS = {
  foods: "ערכי המזון",
  comment: "הערה",
  deleted: "הסרת הדיווח",
};

const RATING_VALUES = [1, 2, 3, 4, 5];

// ============================================================================
// Helpers (presentation only)
// ============================================================================

const fmtNum = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return String(Math.round(num * 10) / 10);
};

const fmtTime = (value) => (value ? formatHebrewDate(value, "HH:mm") : "");

const fmtDateTime = (value) =>
  value ? formatHebrewDate(value, "dd/MM/yyyy HH:mm") : "";

/** A single food row rendered as text — used by the before/after comparison. */
const describeFood = (food) => {
  if (!food) return "—";
  const amount = [fmtNum(food.amount), food.unit].filter(Boolean).join(" ");
  const macros = `${fmtNum(food.calories)} קק"ל`;
  return amount ? `${amount} · ${macros}` : macros;
};

/**
 * Pair the food rows of a `foods` edit by name (duplicated names pair by their
 * order of appearance), so the coach reads "לחם: 2 פרוסות → 1 פרוסה" instead of
 * a JSON dump.
 */
const diffFoods = (before = [], after = []) => {
  const keyOf = (food, seen) => {
    const name = String(food?.name || "פריט").trim() || "פריט";
    const count = (seen[name] || 0) + 1;
    seen[name] = count;
    return `${name}#${count}`;
  };

  const beforeSeen = {};
  const afterSeen = {};
  const beforeMap = new Map(
    (Array.isArray(before) ? before : []).map((food) => [keyOf(food, beforeSeen), food])
  );
  const afterMap = new Map(
    (Array.isArray(after) ? after : []).map((food) => [keyOf(food, afterSeen), food])
  );

  const keys = [...beforeMap.keys()];
  afterMap.forEach((_value, key) => {
    if (!beforeMap.has(key)) keys.push(key);
  });

  return keys
    .map((key) => {
      const beforeFood = beforeMap.get(key);
      const afterFood = afterMap.get(key);
      const name = key.split("#")[0];

      if (!afterFood) return { key, name, kind: "removed", before: describeFood(beforeFood), after: null };
      if (!beforeFood) return { key, name, kind: "added", before: null, after: describeFood(afterFood) };

      const beforeText = describeFood(beforeFood);
      const afterText = describeFood(afterFood);
      if (beforeText === afterText) return null;
      return { key, name, kind: "changed", before: beforeText, after: afterText };
    })
    .filter(Boolean);
};

/**
 * One client edit → the short before/after lines the inline marker shows.
 * Returns [] when there is nothing readable to show (the marker still renders,
 * with a generic line).
 */
const describeClientEdit = (edit) => {
  if (edit.field === "foods") {
    const changes = diffFoods(edit.before, edit.after);
    if (changes.length === 0) return ["עודכנו ערכי המזון של הדיווח"];
    return changes.map((change) => {
      if (change.kind === "added") return `${change.name}: נוסף (${change.after})`;
      if (change.kind === "removed") return `${change.name}: הוסר (היה ${change.before})`;
      return `${change.name}: ${change.before} ← ${change.after}`;
    });
  }
  if (edit.field === "deleted") return ["הדיווח הוסר ע״י הלקוח/ה, ונשמר כאן לצפייה"];
  const before = String(edit.before || "").trim() || "ללא הערה";
  const after = String(edit.after || "").trim() || "ללא הערה";
  return [`${before} ← ${after}`];
};

/** Normalize a food row coming out of the editor into the stored shape. */
const cleanFoodRow = (row) => ({
  name: String(row.name || "").trim(),
  amount: Number(row.amount) || 0,
  unit: String(row.unit || "").trim(),
  calories: Number(row.calories) || 0,
  protein: Number(row.protein) || 0,
  carbohydrates: Number(row.carbohydrates) || 0,
  fat: Number(row.fat) || 0,
});

const emptyFoodRow = () => ({
  rowId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  amount: "",
  unit: "",
  calories: "",
  protein: "",
  carbohydrates: "",
  fat: "",
});

const toEditorRows = (foods = []) =>
  (foods || []).map((food, index) => ({
    rowId: `row-${index}-${food?.name || ""}`,
    name: food?.name ?? "",
    amount: food?.amount ?? "",
    unit: food?.unit ?? "",
    calories: food?.calories ?? "",
    protein: food?.protein ?? "",
    carbohydrates: food?.carbohydrates ?? "",
    fat: food?.fat ?? "",
  }));

// ============================================================================
// Food rows editor (shared by "add on behalf" and "edit an entry")
// ============================================================================

function FoodRowsEditor({ rows, onChange, disabled }) {
  const updateRow = (rowId, key, value) =>
    onChange(rows.map((row) => (row.rowId === rowId ? { ...row, [key]: value } : row)));

  const removeRow = (rowId) => onChange(rows.filter((row) => row.rowId !== rowId));

  const liveTotals = sumNutrition(rows.map(cleanFoodRow));

  return (
    <div className="ndr-editor-foods">
      <div className="ndr-editor-foods-head">
        <span className="ndr-field-label">פריטי המזון</span>
        <button
          type="button"
          className="ndr-ghost-btn"
          onClick={() => onChange([...rows, emptyFoodRow()])}
          disabled={disabled}
        >
          <Plus size={14} weight="bold" />
          <span>הוספת פריט</span>
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="ndr-editor-hint">
          לא נוספו פריטים. אפשר לשמור דיווח טקסט בלבד ולהשלים ערכים מאוחר יותר.
        </p>
      ) : (
        <ul className="ndr-editor-rows">
          {rows.map((row) => (
            <li className="ndr-editor-row" key={row.rowId}>
              <div className="ndr-editor-row-main">
                <label className="ndr-editor-field grow">
                  <span className="ndr-field-label">שם הפריט</span>
                  <input
                    type="text"
                    className="ndr-input"
                    value={row.name}
                    onChange={(event) => updateRow(row.rowId, "name", event.target.value)}
                    disabled={disabled}
                    placeholder="לדוגמה: יוגורט"
                  />
                </label>
                <label className="ndr-editor-field narrow">
                  <span className="ndr-field-label">כמות</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    className="ndr-input ndr-input-num"
                    value={row.amount}
                    onChange={(event) => updateRow(row.rowId, "amount", event.target.value)}
                    disabled={disabled}
                  />
                </label>
                <label className="ndr-editor-field narrow">
                  <span className="ndr-field-label">יחידה</span>
                  <input
                    type="text"
                    className="ndr-input"
                    value={row.unit}
                    onChange={(event) => updateRow(row.rowId, "unit", event.target.value)}
                    disabled={disabled}
                    placeholder="גרם"
                  />
                </label>
                <button
                  type="button"
                  className="ndr-icon-btn danger"
                  onClick={() => removeRow(row.rowId)}
                  disabled={disabled}
                  aria-label={`מחיקת הפריט ${row.name || "ללא שם"}`}
                  title="מחיקת הפריט"
                >
                  <Trash size={14} weight="fill" />
                </button>
              </div>

              <div className="ndr-editor-row-macros">
                {FOOD_MACRO_FIELDS.map((field) => (
                  <label className="ndr-editor-field" key={field.key}>
                    <span className="ndr-field-label">{field.label}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      className="ndr-input ndr-input-num"
                      value={row[field.key]}
                      onChange={(event) => updateRow(row.rowId, field.key, event.target.value)}
                      disabled={disabled}
                    />
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="ndr-editor-totals">
          {MACROS.map((macro) => (
            <span className="ndr-macro-chip" key={macro.key}>
              {macro.label} {fmtNum(liveTotals[macro.key])}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Entry card
// ============================================================================

// ============================================================================
// Inline client-edit marker
// ============================================================================

/**
 * The small purple diamond next to a field the client changed. Before/after
 * on hover (HoverTip) and on tap (inline detail toggled under the entry) —
 * the tooltip is hidden on touch devices, so the tap path is not optional.
 */
function EditMark({ edits, isOpen, onToggle, label }) {
  if (!edits || edits.length === 0) return null;
  const lines = edits.flatMap((edit) => describeClientEdit(edit));
  const tipText = `${label} — שונה ע״י הלקוח/ה: ${lines.join(" · ")}`;

  return (
    <HoverTip text={tipText}>
      <button
        type="button"
        className={`ndr-edit-mark ${isOpen ? "open" : ""}`}
        onClick={onToggle}
        aria-label={`${label} — הלקוח/ה שינה/תה. הצגת לפני/אחרי`}
        aria-expanded={isOpen}
      >
        <WarningDiamond size={12} weight="fill" />
      </button>
    </HoverTip>
  );
}

/** The tapped-open before/after detail for one field. */
function EditMarkDetail({ edits, label }) {
  return (
    <div className="ndr-edit-mark-detail">
      <span className="ndr-edit-mark-detail-title">
        <WarningDiamond size={11} weight="fill" />
        {label} — שונה ע״י הלקוח/ה
      </span>
      {edits.map((edit, index) => (
        <div className="ndr-edit-mark-detail-item" key={`${edit.at}-${index}`}>
          {edit.at && <span className="ndr-edit-meta">{fmtDateTime(edit.at)}</span>}
          <ul className="ndr-edit-mark-lines">
            {describeClientEdit(edit).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function EntryCard({ entry, isSaving, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState(() => toEditorRows(entry.foods));
  const [comment, setComment] = useState(entry.comment || "");
  // Which field's client-edit detail is tapped open (null = none)
  const [openMark, setOpenMark] = useState(null);
  const toggleMark = (field) => setOpenMark((current) => (current === field ? null : field));

  const openEditor = () => {
    setRows(toEditorRows(entry.foods));
    setComment(entry.comment || "");
    setIsEditing(true);
  };

  const handleSave = async () => {
    const foods = rows.map(cleanFoodRow).filter((food) => food.name);
    const saved = await onSave(entry.id, {
      foods,
      comment: comment.trim(),
    });
    if (saved) setIsEditing(false);
  };

  const isClient = entry.source === "client";
  const totals = entry.totals || sumNutrition(entry.foods || []);
  const edits = Array.isArray(entry.edits) ? entry.edits : [];
  const clientEdits = edits.filter((edit) => edit.by === "client");
  // Coach edits stay a plain history list; client edits become inline marks.
  const coachEdits = edits.filter((edit) => edit.by !== "client");
  const clientEditsByField = {
    foods: clientEdits.filter((edit) => edit.field === "foods"),
    comment: clientEdits.filter((edit) => edit.field === "comment"),
    deleted: clientEdits.filter((edit) => edit.field === "deleted"),
  };
  const openMarkEdits = openMark ? clientEditsByField[openMark] : null;

  return (
    <li
      className={`ndr-entry ${entry.deletedByClient ? "removed" : ""} ${
        clientEdits.length > 0 ? "flagged" : ""
      }`}
    >
      <div className="ndr-entry-head">
        <span className={`ndr-badge ${isClient ? "ndr-badge-client" : "ndr-badge-coach"}`}>
          {isClient ? <User size={11} weight="fill" /> : <UserCircleGear size={11} weight="fill" />}
          {isClient ? "דווח ע״י הלקוח/ה" : "נרשם ע״י המאמן/ת"}
        </span>
        {entry.at && <span className="ndr-entry-time">{fmtTime(entry.at)}</span>}
        {entry.ai && (
          <span className="ndr-badge ndr-badge-ai" title="הערכים חושבו בהערכת AI">
            <Robot size={11} weight="fill" />
            הערכת AI
          </span>
        )}
        {entry.ai?.needsDetail && (
          <span className="ndr-badge ndr-badge-warning">חסרים פרטי כמות</span>
        )}
        {entry.deletedByClient && (
          <span className="ndr-badge ndr-badge-removed">הוסר ע״י הלקוח/ה</span>
        )}
        <EditMark
          edits={clientEditsByField.deleted}
          label={EDIT_FIELD_LABELS.deleted}
          isOpen={openMark === "deleted"}
          onToggle={() => toggleMark("deleted")}
        />

        <button
          type="button"
          className="ndr-icon-btn"
          onClick={() => (isEditing ? setIsEditing(false) : openEditor())}
          disabled={isSaving}
          aria-label="עריכת הדיווח"
          title={isEditing ? "סגירת העריכה" : "עריכת הדיווח"}
        >
          <PencilSimple size={14} weight="fill" />
        </button>
      </div>

      {entry.rawText && <p className="ndr-entry-raw">{entry.rawText}</p>}

      {(entry.foods || []).length > 0 && (
        <ul className="ndr-food-list">
          {entry.foods.map((food, index) => (
            <li className="ndr-food-row" key={`${food.name}-${index}`}>
              <span className="ndr-food-name">{food.name || "פריט"}</span>
              <span className="ndr-food-amount">
                {fmtNum(food.amount)} {food.unit}
              </span>
              <span className="ndr-food-macros">
                {MACROS.map((macro) => (
                  <span className="ndr-macro-chip" key={macro.key}>
                    {macro.label} {fmtNum(food[macro.key])}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="ndr-entry-totals">
        <EditMark
          edits={clientEditsByField.foods}
          label={EDIT_FIELD_LABELS.foods}
          isOpen={openMark === "foods"}
          onToggle={() => toggleMark("foods")}
        />
        {MACROS.map((macro) => (
          <span className="ndr-macro-chip strong" key={macro.key}>
            {macro.label} {fmtNum(totals?.[macro.key])}
          </span>
        ))}
      </div>

      {/* A comment the client cleared still gets its marker — the coach must
          see that a note used to be here */}
      {(entry.comment || clientEditsByField.comment.length > 0) && (
        <p className={`ndr-entry-comment ${entry.comment ? "" : "empty"}`}>
          <NotePencil size={11} weight="fill" />
          {entry.comment || "ללא הערה"}
          <EditMark
            edits={clientEditsByField.comment}
            label={EDIT_FIELD_LABELS.comment}
            isOpen={openMark === "comment"}
            onToggle={() => toggleMark("comment")}
          />
        </p>
      )}

      {openMarkEdits && openMarkEdits.length > 0 && (
        <EditMarkDetail edits={openMarkEdits} label={EDIT_FIELD_LABELS[openMark]} />
      )}

      {coachEdits.length > 0 && (
        <div className="ndr-edits">
          <span className="ndr-edits-title">
            <WarningDiamond size={11} weight="fill" />
            היסטוריית עריכות
          </span>

          <ul className="ndr-edits-list">
            {coachEdits.map((edit, index) => {
              const byClient = edit.by === "client";
              const label = EDIT_FIELD_LABELS[edit.field] || "שינוי";
              const foodChanges =
                edit.field === "foods" ? diffFoods(edit.before, edit.after) : [];

              return (
                <li className={`ndr-edit ${byClient ? "client" : "coach"}`} key={`${edit.at}-${index}`}>
                  <div className="ndr-edit-head">
                    <span className="ndr-edit-field">{label}</span>
                    <span className="ndr-edit-meta">
                      {byClient ? "ע״י הלקוח/ה" : "ע״י המאמן/ת"}
                      {edit.at ? ` · ${fmtDateTime(edit.at)}` : ""}
                    </span>
                  </div>

                  {edit.field === "foods" ? (
                    foodChanges.length === 0 ? (
                      <p className="ndr-edit-note">עודכנו ערכי המזון של הדיווח</p>
                    ) : (
                      <ul className="ndr-edit-diff">
                        {foodChanges.map((change) => (
                          <li className="ndr-edit-diff-row" key={change.key}>
                            <span className="ndr-edit-diff-name">{change.name}</span>
                            <span className="ndr-edit-before">
                              {change.kind === "added" ? "לא היה בדיווח" : change.before}
                            </span>
                            <ArrowRight size={11} weight="bold" className="ndr-edit-arrow" />
                            <span className="ndr-edit-after">
                              {change.kind === "removed" ? "הוסר" : change.after}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )
                  ) : edit.field === "deleted" ? (
                    <p className="ndr-edit-note">הדיווח הוסר, אך נשמר כאן לצפייה</p>
                  ) : (
                    <div className="ndr-edit-diff-row">
                      <span className="ndr-edit-before">
                        {String(edit.before || "").trim() || "ללא הערה"}
                      </span>
                      <ArrowRight size={11} weight="bold" className="ndr-edit-arrow" />
                      <span className="ndr-edit-after">
                        {String(edit.after || "").trim() || "ללא הערה"}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {isEditing && (
        <div className="ndr-editor">
          <FoodRowsEditor rows={rows} onChange={setRows} disabled={isSaving} />

          <label className="ndr-editor-field">
            <span className="ndr-field-label">הערה לדיווח</span>
            <textarea
              className="ndr-input ndr-textarea"
              rows={2}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              disabled={isSaving}
              placeholder="הערה שתישמר לצד הדיווח"
            />
          </label>

          <div className="ndr-editor-actions">
            <button
              type="button"
              className="ndr-secondary-btn"
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
            >
              ביטול
            </button>
            <button
              type="button"
              className="ndr-primary-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>שומר...</span>
                </>
              ) : (
                <span>שמירת השינויים</span>
              )}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Day card
// ============================================================================

function DayCard({
  report,
  goals,
  isExpanded,
  onToggle,
  isSaving,
  onAddEntry,
  onUpdateEntry,
  onMarkReviewed,
  onAddNote,
  onDeleteNote,
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [draftRows, setDraftRows] = useState([]);
  const [draftText, setDraftText] = useState("");
  const [draftComment, setDraftComment] = useState("");
  const [ratingDraft, setRatingDraft] = useState(report.rating || 0);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const entries = Array.isArray(report.entries) ? report.entries : [];
  const totals = report.totals || {};
  const isReviewed = report.coachReviewed === true;
  const hasClientEdits = report.hasClientEdits === true;

  const resetDraft = () => {
    setDraftRows([]);
    setDraftText("");
    setDraftComment("");
  };

  const handleAdd = async () => {
    const foods = draftRows.map(cleanFoodRow).filter((food) => food.name);
    const rawText = draftText.trim();
    if (!rawText && foods.length === 0) return;

    const saved = await onAddEntry(report.id, {
      rawText,
      comment: draftComment.trim(),
      foods,
    });
    if (saved) {
      resetDraft();
      setIsAdding(false);
    }
  };

  const handleReview = async () => {
    const saved = await onMarkReviewed(report.id, ratingDraft || null);
    if (saved) setIsReviewOpen(false);
  };

  return (
    <li className={`ndr-day ${hasClientEdits ? "flagged" : ""}`}>
      <button
        type="button"
        className="ndr-day-head"
        onClick={() => onToggle(report.id)}
        aria-expanded={isExpanded}
      >
        <span className="ndr-day-head-main">
          <span className="ndr-day-date">{formatHebrewDate(report.date || report.id)}</span>
          <span className="ndr-day-meta">
            {entries.length} דיווחים
            {report.status === "open" ? " · היום עדיין פתוח" : ""}
          </span>
        </span>

        <span className="ndr-day-badges">
          {hasClientEdits && (
            <span className="ndr-badge ndr-badge-edits">
              <WarningDiamond size={11} weight="fill" />
              הלקוח/ה שינה/תה דיווח
            </span>
          )}
          {isReviewed ? (
            <span className="ndr-badge ndr-badge-reviewed">
              <CheckCircle size={11} weight="fill" />
              נסקר
              {report.rating ? ` · ${report.rating}/5` : ""}
            </span>
          ) : (
            <span className="ndr-badge ndr-badge-pending">
              <ClockCountdown size={11} weight="fill" />
              ממתין לסקירה
            </span>
          )}
          <CaretDown
            size={14}
            weight="bold"
            className={`ndr-day-caret ${isExpanded ? "open" : ""}`}
          />
        </span>
      </button>

      <div className="ndr-macro-grid">
        {MACROS.map((macro) => {
          const actual = Number(totals?.[macro.key]) || 0;
          const goal = Number(goals?.[macro.key]) || 0;
          const progress = calcGoalProgress(actual, goal);
          const statusClass = GOAL_STATUS_CLASS[progress.status] || "";

          return (
            <div className={`ndr-macro ${statusClass}`} key={macro.key}>
              <span className="ndr-macro-label">{macro.label}</span>
              <span className="ndr-macro-value">
                <strong>{fmtNum(actual)}</strong>
                {progress.hasGoal && <span className="ndr-macro-goal">/ {fmtNum(goal)}</span>}
                <span className="ndr-macro-unit">{macro.unit}</span>
              </span>
              {progress.hasGoal ? (
                <>
                  <span
                    className="ndr-macro-track"
                    role="progressbar"
                    aria-valuenow={progress.percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${macro.label} ביחס ליעד`}
                  >
                    <span
                      className="ndr-macro-fill"
                      style={{ width: `${progress.barPercent}%` }}
                    />
                  </span>
                  <span className="ndr-macro-percent">{progress.percent}% מהיעד</span>
                </>
              ) : (
                <span className="ndr-macro-percent muted">ללא יעד</span>
              )}
            </div>
          );
        })}
      </div>

      {isExpanded && (
        <div className="ndr-day-body">
          {entries.length === 0 ? (
            <p className="ndr-day-empty">לא נרשמו דיווחי אכילה ביום הזה</p>
          ) : (
            <ul className="ndr-entries">
              {entries.map((entry) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  isSaving={isSaving}
                  onSave={(entryId, changes) => onUpdateEntry(report.id, entryId, changes)}
                />
              ))}
            </ul>
          )}

          {/* Log on the client's behalf */}
          {isAdding ? (
            <div className="ndr-editor standalone">
              <span className="ndr-editor-title">הוספת דיווח בשם הלקוח/ה</span>

              <label className="ndr-editor-field">
                <span className="ndr-field-label">תיאור הארוחה</span>
                <textarea
                  className="ndr-input ndr-textarea"
                  rows={2}
                  value={draftText}
                  onChange={(event) => setDraftText(event.target.value)}
                  disabled={isSaving}
                  placeholder="לדוגמה: סלט עם טונה וחביתה"
                />
              </label>

              <FoodRowsEditor rows={draftRows} onChange={setDraftRows} disabled={isSaving} />

              <label className="ndr-editor-field">
                <span className="ndr-field-label">הערה לדיווח</span>
                <textarea
                  className="ndr-input ndr-textarea"
                  rows={2}
                  value={draftComment}
                  onChange={(event) => setDraftComment(event.target.value)}
                  disabled={isSaving}
                  placeholder="הערה שתישמר לצד הדיווח"
                />
              </label>

              <div className="ndr-editor-actions">
                <button
                  type="button"
                  className="ndr-secondary-btn"
                  onClick={() => setIsAdding(false)}
                  disabled={isSaving}
                >
                  ביטול
                </button>
                <button
                  type="button"
                  className="ndr-primary-btn"
                  onClick={handleAdd}
                  disabled={isSaving || (!draftText.trim() && draftRows.length === 0)}
                >
                  {isSaving ? (
                    <>
                      <LoaderLoop size="xs" />
                      <span>שומר...</span>
                    </>
                  ) : (
                    <span>שמירת הדיווח</span>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="ndr-secondary-btn wide"
              onClick={() => setIsAdding(true)}
            >
              <Plus size={14} weight="bold" />
              <span>הוספת דיווח בשם הלקוח/ה</span>
            </button>
          )}

          {/* Review — the deliberate action that clears the pending queue */}
          <div className="ndr-review">
            <div className="ndr-review-head">
              <span className="ndr-field-label">סקירת היום</span>
              {isReviewed && (
                <span className="ndr-review-state">
                  נסקר
                  {report.reviewedAt ? ` · ${fmtDateTime(report.reviewedAt)}` : ""}
                  {report.rating ? ` · דירוג ${report.rating}/5` : ""}
                </span>
              )}
            </div>

            {isReviewed && !isReviewOpen ? (
              <button
                type="button"
                className="ndr-secondary-btn wide"
                onClick={() => {
                  setRatingDraft(report.rating || 0);
                  setIsReviewOpen(true);
                }}
              >
                <PencilSimple size={14} weight="fill" />
                <span>עדכון הסקירה והדירוג</span>
              </button>
            ) : (
              <>
                <div
                  className="ndr-rating"
                  role="group"
                  aria-label="דירוג היום התזונתי, בין 1 ל-5"
                >
                  {RATING_VALUES.map((value) => {
                    const isOn = ratingDraft >= value;
                    return (
                      <button
                        key={value}
                        type="button"
                        className={`ndr-star ${isOn ? "on" : ""}`}
                        onClick={() => setRatingDraft(ratingDraft === value ? 0 : value)}
                        aria-pressed={isOn}
                        aria-label={`דירוג ${value} מתוך 5`}
                        title={`דירוג ${value} מתוך 5`}
                      >
                        <Star size={16} weight={isOn ? "fill" : "regular"} />
                      </button>
                    );
                  })}
                  <span className="ndr-rating-hint">
                    {ratingDraft > 0 ? `${ratingDraft} מתוך 5` : "דירוג הוא רשות"}
                  </span>
                </div>

                <div className="ndr-editor-actions">
                  {isReviewOpen && (
                    <button
                      type="button"
                      className="ndr-secondary-btn"
                      onClick={() => setIsReviewOpen(false)}
                      disabled={isSaving}
                    >
                      ביטול
                    </button>
                  )}
                  <button
                    type="button"
                    className="ndr-primary-btn"
                    onClick={handleReview}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <LoaderLoop size="xs" />
                        <span>שומר...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} weight="fill" />
                        <span>{isReviewed ? "שמירת הסקירה" : "סימון כנסקר"}</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          <CoachNotesPanel
            notes={report.coachNotes || []}
            onAdd={(content) => onAddNote(report.id, content)}
            onDelete={(noteId) => onDeleteNote(report.id, noteId)}
            title="הערות המאמן/ת ליום זה"
          />
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Tab
// ============================================================================

function DailyReportsTab({ clinicId, patientId, patient }) {
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();

  const [filter, setFilter] = useState("all");
  const [expandedIds, setExpandedIds] = useState([]);

  const {
    data: reports = [],
    isLoading,
    error: loadError,
    refetch,
  } = useGetDailyReportsQuery(
    { clinicId, patientId, limitCount: REPORTS_LIMIT },
    { skip: !clinicId || !patientId }
  );

  const [addCoachFoodEntry, { isLoading: isAddingEntry }] = useAddCoachFoodEntryMutation();
  const [updateFoodEntry, { isLoading: isUpdatingEntry }] = useUpdateFoodEntryMutation();
  const [markReportReviewed, { isLoading: isReviewing }] = useMarkReportReviewedMutation();
  const [addCoachNote] = useAddCoachNoteMutation();
  const [deleteCoachNote] = useDeleteCoachNoteMutation();

  const isSaving = isAddingEntry || isUpdatingEntry || isReviewing;

  useEffect(() => {
    if (!loadError) return;
    console.error("[CK-10200] Error loading daily eating reports:", {
      errorCode: "CK-10200",
      errorMessage: loadError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [loadError, clinicId, patientId]);

  /** A day without a closing snapshot falls back to the client's live goals. */
  const fallbackGoals = patient?.nutrition?.goals || null;

  const counts = useMemo(
    () => ({
      all: reports.length,
      pending: reports.filter((report) => report.coachReviewed !== true).length,
    }),
    [reports]
  );

  const visibleReports = useMemo(() => {
    if (filter === "pending") return reports.filter((r) => r.coachReviewed !== true);
    return reports;
  }, [reports, filter]);

  const toggleDay = useCallback((dateKey) => {
    setExpandedIds((current) =>
      current.includes(dateKey)
        ? current.filter((id) => id !== dateKey)
        : [...current, dateKey]
    );
  }, []);

  // ─── Mutations. Each returns true on success so the child can close itself ───
  const handleAddEntry = useCallback(
    async (dateKey, entryData) => {
      try {
        await addCoachFoodEntry({
          clinicId,
          patientId,
          dateKey,
          entryData,
          therapistId,
          therapistName,
        }).unwrap();
        showSuccess("הדיווח נוסף לדוח היומי");
        return true;
      } catch (error) {
        console.error("[CK-10201] Error adding a coach food entry:", {
          errorCode: "CK-10201",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          dateKey,
          timestamp: new Date().toISOString(),
        });
        showError(
          error?.message || error?.data?.message || "הוספת הדיווח נכשלה, נסו שוב"
        );
        return false;
      }
    },
    [addCoachFoodEntry, clinicId, patientId, therapistId, therapistName, showSuccess, showError]
  );

  const handleUpdateEntry = useCallback(
    async (dateKey, entryId, changes) => {
      try {
        await updateFoodEntry({
          clinicId,
          patientId,
          dateKey,
          entryId,
          changes,
          therapistId,
          therapistName,
        }).unwrap();
        showSuccess("הדיווח עודכן");
        return true;
      } catch (error) {
        console.error("[CK-10202] Error updating a food entry:", {
          errorCode: "CK-10202",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          dateKey,
          timestamp: new Date().toISOString(),
        });
        showError(
          error?.message || error?.data?.message || "עדכון הדיווח נכשל, נסו שוב"
        );
        return false;
      }
    },
    [updateFoodEntry, clinicId, patientId, therapistId, therapistName, showSuccess, showError]
  );

  const handleMarkReviewed = useCallback(
    async (dateKey, rating) => {
      try {
        await markReportReviewed({
          clinicId,
          patientId,
          dateKey,
          rating,
          therapistId,
          therapistName,
        }).unwrap();
        showSuccess("היום סומן כנסקר");
        return true;
      } catch (error) {
        console.error("[CK-10203] Error marking a daily report reviewed:", {
          errorCode: "CK-10203",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          dateKey,
          timestamp: new Date().toISOString(),
        });
        showError(
          error?.message || error?.data?.message || "סימון הסקירה נכשל, נסו שוב"
        );
        return false;
      }
    },
    [markReportReviewed, clinicId, patientId, therapistId, therapistName, showSuccess, showError]
  );

  /**
   * CoachNotesPanel surfaces its own Hebrew error toast, so these two log the
   * CK context and RE-THROW instead of swallowing the rejection.
   */
  const handleAddNote = useCallback(
    async (dateKey, content) => {
      try {
        await addCoachNote({
          clinicId,
          patientId,
          target: NOTE_TARGETS.DAILY_REPORT,
          docId: dateKey,
          content,
          therapistId,
          therapistName,
        }).unwrap();
      } catch (error) {
        console.error("[CK-10204] Error adding a note to a daily report:", {
          errorCode: "CK-10204",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          dateKey,
          timestamp: new Date().toISOString(),
        });
        throw error;
      }
    },
    [addCoachNote, clinicId, patientId, therapistId, therapistName]
  );

  const handleDeleteNote = useCallback(
    async (dateKey, noteId) => {
      // arrayRemove needs the exact stored object, but the panel only knows ids.
      const report = reports.find((item) => item.id === dateKey);
      const note = (report?.coachNotes || []).find((item) => item.id === noteId);
      if (!note) {
        const missing = new Error("ההערה לא נמצאה");
        console.error("[CK-10205] Error deleting a note from a daily report:", {
          errorCode: "CK-10205",
          errorMessage: "note not found",
          clinicId,
          patientId,
          dateKey,
          timestamp: new Date().toISOString(),
        });
        throw missing;
      }

      try {
        await deleteCoachNote({
          clinicId,
          patientId,
          target: NOTE_TARGETS.DAILY_REPORT,
          docId: dateKey,
          note,
        }).unwrap();
      } catch (error) {
        console.error("[CK-10205] Error deleting a note from a daily report:", {
          errorCode: "CK-10205",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          dateKey,
          timestamp: new Date().toISOString(),
        });
        throw error;
      }
    },
    [deleteCoachNote, reports, clinicId, patientId]
  );

  // ─── Render ───
  const renderBody = () => {
    if (isLoading) {
      return (
        <ContentLoader fill message="טוען דוחות אכילה..." />
      );
    }

    if (loadError) {
      return (
        <div className="ndr-empty">
          <EmptyDraw variant="search" />
          <p>טעינת דוחות האכילה נכשלה</p>
          <button type="button" className="ndr-secondary-btn" onClick={() => refetch()}>
            נסו שוב
          </button>
        </div>
      );
    }

    if (reports.length === 0) {
      return (
        <div className="ndr-empty">
          <EmptyDraw variant="treatment" />
          <p>עדיין אין דוחות אכילה עבור לקוח/ה זה</p>
          <p className="ndr-empty-hint">
            דוחות נפתחים כשהלקוח/ה מדווח/ת ארוחה בעמוד האישי, ונסגרים אוטומטית
            בכל בוקר. הצעד הבא: שלחו ללקוח/ה את הקישור.
          </p>
          <div className="ndr-empty-link">
            <PersonalPageLink clinicId={clinicId} patient={patient} variant="compact" />
          </div>
        </div>
      );
    }

    if (visibleReports.length === 0) {
      return (
        <div className="ndr-empty">
          <EmptyDraw variant="done" />
          <p>כל הדוחות נסקרו — אין ימים שממתינים לכם</p>
        </div>
      );
    }

    return (
      <ul className="ndr-days">
        {visibleReports.map((report) => (
          <DayCard
            key={report.id}
            report={report}
            goals={report.goals || fallbackGoals}
            isExpanded={expandedIds.includes(report.id)}
            onToggle={toggleDay}
            isSaving={isSaving}
            onAddEntry={handleAddEntry}
            onUpdateEntry={handleUpdateEntry}
            onMarkReviewed={handleMarkReviewed}
            onAddNote={handleAddNote}
            onDeleteNote={handleDeleteNote}
          />
        ))}
      </ul>
    );
  };

  return (
    <div className="ndr-tab">
      <div className="ndr-header">
        <div className="ndr-header-title">
          <span className="ndr-header-icon">
            <ForkKnife size={20} weight="fill" />
          </span>
          <div className="ndr-header-text">
            <span className="ndr-eyebrow">מעקב תזונתי</span>
            <h3>דוחות אכילה</h3>
          </div>
        </div>
        {counts.pending > 0 && (
          <span className="ndr-badge ndr-badge-pending">
            <ClockCountdown size={11} weight="fill" />
            {counts.pending} ימים ממתינים לסקירה
          </span>
        )}
      </div>

      <div className="ndr-card">
        <div className="ndr-tabs" role="tablist" aria-label="סינון דוחות האכילה">
          {FILTER_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = filter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`ndr-tab-btn ${isActive ? "active" : ""}`}
                onClick={() => setFilter(tab.id)}
              >
                <Icon size={15} weight={isActive ? "fill" : "regular"} />
                <span>{tab.label}</span>
                <span className="ndr-tab-count">{counts[tab.id]}</span>
              </button>
            );
          })}
        </div>

        <div className="ndr-body">{renderBody()}</div>
      </div>
    </div>
  );
}

export default DailyReportsTab;
